import React, { useState, useEffect } from 'react';
import { Plus, FileText } from "lucide-react";
import { AddDesign } from "../../client_contact_stages/verve-design/components/AddDesign";
import { designService } from "../../client_contact_stages/verve-design/services/designService";
import API, { BASE_URL } from "@/lib/API";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import toast from 'react-hot-toast';
import { useAuthStore } from "@/store/authStore";

import { LeadExecutionTable } from './lead_ececution_table';
import { LeadExecutionDocumentViewer } from './LeadExecutionDocumentViewer';
import { ActionView, ActionDownload, ActionApprove, ActionDelete } from "@/components/ui/LatestTableActions";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";

export const DesignsTab = ({ project, onUpdate }) => {
  const { user: currentUser } = useAuthStore();
  const [designs, setDesigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // View Modal state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Delete Modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchDesigns = async () => {
    try {
      setIsLoading(true);
      const res = await API.get(`/lead-executions-designs/${project._id}/designs`);
      setDesigns(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching designs:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project?._id) {
      fetchDesigns();
    }
  }, [project?._id]);

  const handleUploadSubmit = async (formDataArray) => {
    try {
      if (formDataArray && formDataArray.length > 0) {
        for (const formData of formDataArray) {
          await designService.uploadMedia(formData);
        }
        showSuccessMessage("Design(s) uploaded successfully");
      }
      setIsAddModalOpen(false);
      fetchDesigns();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleView = (file) => {
    const url = file.url || `${BASE_URL}/api/design/media/${file.fileId}/${file.filename}`;
    setViewerFile({ url, name: file.filename || file.name });
    setViewerOpen(true);
  };
  
  const handleApprove = async (file) => {
    try {
      await API.patch(`/design/${file.designId}/${file.fileId}/approve`);
      showSuccessMessage("Design approved successfully");
      fetchDesigns();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleReject = (file) => {
    setFileToDelete(file);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    setIsDeleting(true);
    try {
      await designService.deleteDesignMedia(fileToDelete.designId, fileToDelete.fileId);
      showSuccessMessage("Design deleted successfully");
      fetchDesigns();
    } catch (err) {
      showApiError(err);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setFileToDelete(null);
    }
  };

  const handleDownload = async (file) => {
    const url = file.url || `${BASE_URL}/api/design/media/${file.fileId}/${file.filename}`;
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}` // pass token in case it's protected
        }
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename || file.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download file");
    }
  };

  const columns = [
    {
      header: "File Name",
      render: (file) => (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors whitespace-nowrap"
          onClick={() => handleView(file)}
        >
          <FileText className="w-4 h-4 text-slate-400" />
          <span className="font-semibold text-[#65768e] text-[11px] truncate max-w-[200px]" title={file.filename || file.name}>
            {file.filename || file.name}
          </span>
        </div>
      )
    },
    {
      header: "Stage",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold capitalize whitespace-nowrap">
          {(file.stage || 'other').replace(/_/g, ' ')}
        </span>
      )
    },
    {
      header: "Status",
      render: (file) => {
        const isApproved = file.status === 'Approved';
        return (
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${isApproved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        );
      }
    },
    {
      header: "Size",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold whitespace-nowrap">
          {file.size ? (file.size / 1024 / 1024).toFixed(2) : '0'} MB
        </span>
      )
    },
    {
      header: "Uploaded By",
      render: (file) => (
        <div className="flex flex-col whitespace-nowrap">
          <span className="text-[#65768e] text-[11px] font-semibold">{file.uploadedBy?.name || '—'}</span>
          {file.uploadedBy?.role && (
            <span className="text-slate-400 text-[9px] uppercase tracking-wider font-bold mt-0.5">
              {file.uploadedBy.role.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )
    },
    {
      header: "Date",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold whitespace-nowrap">
          {new Date(file.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      )
    },
    {
      header: "Actions",
      render: (file) => {
        const isApproved = file.status === 'Approved';
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <ActionView onClick={() => handleView(file)} title="View" />
            <ActionDownload onClick={() => handleDownload(file)} title="Download" />
            {!isApproved && currentUser?.role && ['senior_designer', 'admin', 'owner'].includes(currentUser.role) && <ActionApprove onClick={() => handleApprove(file)} title="Approve" />}
            <ActionDelete onClick={() => handleReject(file)} title="Delete" />
          </div>
        );
      }
    }
  ];

  return (
    <div className="w-full flex flex-col animate-in fade-in duration-500">
      <div className="flex justify-end items-center px-1 mb-2">
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-[11px] font-bold tracking-wide uppercase border border-[#2b448f] text-[#2b448f] bg-white rounded-md hover:bg-slate-50 transition-colors shadow-sm ml-auto"
        >
          <Plus className="w-3.5 h-3.5" />
          Upload Design
        </button>
      </div>

      <LeadExecutionTable 
        columns={columns} 
        data={designs.filter(d => d.stage !== 'sign_of_copy' && d.stage !== 'documents' && d.stage !== 'gallery')} 
        isLoading={isLoading} 
      />

      <AddDesign
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleUploadSubmit}
        initialData={project.leadId || project.leadDetails}
        titleOverride="Upload Design"
      />
      
      <LeadExecutionDocumentViewer 
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Design"
        message="Are you sure you want to delete this design? This action will also permanently remove the file from Google Drive."
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default DesignsTab;
